import { useCallback, useEffect, useState } from "react";
import Api, { QuoteWithClient } from "../../Api";
import Section from "../../parts/Section";
import { Quote } from "../../types";
import DiscreetMessage from "../../parts/DiscreetMessage";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ContractForm from "./ContractForm";

const ContractsDashboard = () => {
  const [items, setItems] = useState<QuoteWithClient[] | null>([]);
  const [selected, setSelected] = useState<string | null>(null);

  const loadData = useCallback(() => Api.contractsGet().then(setItems), []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (items === null) {
    return <DiscreetMessage text="Carregando contratos" />;
  }

  return (
    <div className="flex gap-8">
      <Section className="p-4 pb-32 flex flex-col gap-4">
        <div className="bg-gray-100 text-sm font-bold p-2 rounded-lg text-center">
          Contratos pendentes: {items.length}
        </div>

        <div className="flex flex-col gap-4">
          {items.map(({ quote, client }) => (
            <QuoteItem
              key={quote.uuid}
              quote={quote}
              clientName={client.name}
              expanded={selected === quote.uuid}
              onClick={() =>
                setSelected(selected === quote.uuid ? null : quote.uuid)
              }
              onSave={loadData}
            />
          ))}
        </div>
      </Section>
    </div>
  );
};

export default ContractsDashboard;

const QuoteItem = ({
  quote,
  clientName,
  expanded,
  onClick,
  onSave,
}: {
  quote: Quote;
  clientName: string;
  expanded: boolean;
  onClick: () => void;
  onSave: () => void;
}) => {
  const { rentState, monitoringState } = getContractState(quote);

  const complete = () => {
    const message =
      quote.itemsRented.length > 0 && quote.feeMonitoring > 0
        ? "Confirmar assinatura dos contratos?"
        : "Confirmar assinatura do contrato?";
    window.confirm(message) && Api.contractsComplete(quote.uuid).then(onSave);
  };

  return (
    <div className="border rounded-lg">
      <div
        className={classNames(
          "flex items-stretch cursor-pointer hover:bg-slate-50 rounded-lg gap-2",
          {
            "rounded-b-none border-b": expanded,
          }
        )}
        onClick={onClick}
      >
        <div className="flex items-center gap-1 pl-2">
          <Ball state={rentState} />
          <Ball state={monitoringState} />
        </div>

        <div className="truncate flex-1 text-sm font-bold py-2 px">
          {clientName}
        </div>

        <div
          className={classNames(
            "bg-gray-100 text-gray-600 py-1 px-2 text-xs font-semibold rounded-r-lg border-l flex items-center",
            {
              "rounded-br-none": expanded,
            }
          )}
        >
          {quote.number}
        </div>
      </div>

      {expanded && (
        <div className="flex flex-col">
          {quote.itemsRented.length > 0 && (
            <ContractForm
              uuid={quote.uuid}
              type="rent"
              contractInfo={quote.rentContractInfo}
              onSave={onSave}
            />
          )}

          {quote.feeMonitoring > 0 && (
            <ContractForm
              uuid={quote.uuid}
              type="monitoring"
              contractInfo={quote.monitoringContractInfo}
              onSave={onSave}
            />
          )}

          <div className="p-4">
            <button
              onClick={complete}
              className={classNames(
                "rounded p-1 w-full font-bold bg-gray-50 text-gray-400",
                {
                  "bg-sky-600 text-white": canComplete(quote),
                }
              )}
              disabled={!canComplete(quote)}
            >
              <FontAwesomeIcon icon="signature" /> Concluído
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const canComplete = (quote: Quote) => {
  return (
    (quote.itemsRented.length <= 0 || quote.rentContractInfo?.signed) &&
    (quote.feeMonitoring <= 0 || quote.monitoringContractInfo?.signed)
  );
};

type ContractState = "null" | "pending" | "waiting" | "signed";
type ContractStateMap = {
  rentState: ContractState;
  monitoringState: ContractState;
};
const getContractState = (quote: Quote): ContractStateMap => {
  const state: ContractStateMap = {
    rentState: "null",
    monitoringState: "null",
  };

  if (quote.itemsRented.length > 0)
    state.rentState =
      quote.rentContractInfo === null || !quote.rentContractInfo.sent
        ? "pending"
        : quote.rentContractInfo.signed
          ? "signed"
          : "waiting";

  if (quote.feeMonitoring > 0)
    state.monitoringState =
      quote.monitoringContractInfo === null ||
      !quote.monitoringContractInfo.sent
        ? "pending"
        : quote.monitoringContractInfo.signed
          ? "signed"
          : "waiting";

  return state;
};

const Ball = ({ state }: { state: ContractState }) => (
  <div
    className={classNames("w-2.5 h-2.5 rounded-full", {
      "bg-gray-300": state === "null",
      "bg-red-500": state === "pending",
      "bg-yellow-400": state === "waiting",
      "bg-green-400": state === "signed",
    })}
  />
);
